import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Package, UserCircle, Building, ShoppingCart, List } from "lucide-react";

interface TenantStatsTabProps {
  tenantId: string;
}

export function TenantStatsTab({ tenantId }: TenantStatsTabProps) {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["tenant-stats", tenantId],
    queryFn: async () => {
      const [profiles, products, clients, suppliers, transactions, transactionItems] =
        await Promise.all([
          supabase
            .from("profiles")
            .select("id", { count: "exact", head: true })
            .eq("tenant_id", tenantId),
          supabase
            .from("products")
            .select("id", { count: "exact", head: true })
            .eq("tenant_id", tenantId),
          supabase
            .from("clients")
            .select("id", { count: "exact", head: true })
            .eq("tenant_id", tenantId),
          supabase
            .from("suppliers")
            .select("id", { count: "exact", head: true })
            .eq("tenant_id", tenantId),
          supabase
            .from("transactions")
            .select("id", { count: "exact", head: true })
            .eq("tenant_id", tenantId),
          supabase
            .from("transaction_items")
            .select("id, transaction_id!inner(tenant_id)", { count: "exact", head: true })
            .eq("transaction_id.tenant_id", tenantId),
        ]);

      return {
        users: profiles.count || 0,
        products: products.count || 0,
        clients: clients.count || 0,
        suppliers: suppliers.count || 0,
        transactions: transactions.count || 0,
        transactionItems: transactionItems.count || 0,
      };
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Usuários",
      value: stats?.users || 0,
      icon: Users,
      description: "Usuários cadastrados",
    },
    {
      title: "Produtos",
      value: stats?.products || 0,
      icon: Package,
      description: "Produtos no catálogo",
    },
    {
      title: "Clientes",
      value: stats?.clients || 0,
      icon: UserCircle,
      description: "Clientes cadastrados",
    },
    {
      title: "Fornecedores",
      value: stats?.suppliers || 0,
      icon: Building,
      description: "Fornecedores cadastrados",
    },
    {
      title: "Transações",
      value: stats?.transactions || 0,
      icon: ShoppingCart,
      description: "Total de transações",
    },
    {
      title: "Itens de Transações",
      value: stats?.transactionItems || 0,
      icon: List,
      description: "Itens em transações",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {statCards.map((stat) => (
        <Card key={stat.title}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
            <stat.icon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stat.value}</div>
            <p className="text-xs text-muted-foreground">{stat.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
